/**
 * 入庫 · 收貨對數表單（`ui-spec` 頁 6）—— 按「確認收貨」就**即時入帳**
 *
 * 規則（同後端 `POST /api/intakes/:id/verify` 一致；前端先擋係為咗好體驗，**後端才係權威**）：
 *  ① 要一次過對齊**全部**行（唔准部分收貨）
 *  ② 實收 ≠ 申報 → **必填原因**（`project-spec` A2）
 *  ③ 損壞 ≤ 實收
 *  ④ 有損壞 → **一定要有箱相**（`rules §七 B4`）：相先經 `POST /api/evidence` 上載，再引用 id
 *  ⑤ 一按確認 → 好貨入 `warehouse`、損壞入 `damaged`（照計租、唔出貨；`decisions/008`）
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import FormTextField from '@coeldery/form-text-field';
import { btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import IntakeVerifyRow, { type VerifyRowState } from './IntakeVerifyRow';
import { ApiError, api, type IntakeBatch, type IntakeLine } from '../lib/api';

export interface IntakeVerifyFormProps {
  batch: IntakeBatch;
  lines: IntakeLine[];
  onDone: () => Promise<void>;
}

export default function IntakeVerifyForm({ batch, lines, onDone }: IntakeVerifyFormProps) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [receivedDate, setReceivedDate] = useState<string | null>(today);
  const [rows, setRows] = useState<Record<string, VerifyRowState>>(() =>
    Object.fromEntries(
      lines.map((l) => [
        l.id,
        {
          receivedCartons: String(l.declaredCartons),
          damagedCartons: '0',
          note: '',
          evidenceIds: [] as string[],
        },
      ]),
    ),
  );
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const row = (id: string): VerifyRowState =>
    rows[id] ?? { receivedCartons: '0', damagedCartons: '0', note: '', evidenceIds: [] };

  const patchRow = (id: string, patch: Partial<VerifyRowState>) =>
    setRows((r) => ({ ...r, [id]: { ...row(id), ...patch } }));

  /** 上載損壞箱相（先 POST /api/evidence，再引用 id 落 verify） */
  const uploadPhoto = async (lineId: string, file: File) => {
    setUploadingId(lineId);
    setError(null);
    try {
      const res = await api.uploadEvidence(
        { entityType: 'intake_line', entityId: lineId, kind: 'damage', note: t('intake.damagePhoto') },
        file,
      );
      patchRow(lineId, { evidenceIds: [...row(lineId).evidenceIds, res.id] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setUploadingId(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(false);

    // ① 前端先驗（同後端規則一致）
    for (const l of lines) {
      const r = row(l.id);
      const received = Number(r.receivedCartons);
      const damaged = Number(r.damagedCartons);
      const name = l.itemName ?? l.barcode ?? '';
      if (!Number.isInteger(received) || received < 0) {
        return setError(`${name}：${t('intake.receivedCartons')} ≥ 0`);
      }
      if (!Number.isInteger(damaged) || damaged < 0 || damaged > received) {
        return setError(`${name}：${t('intake.damagedCartons')} 0–${received}`);
      }
      if (received !== l.declaredCartons && !r.note.trim()) {
        return setError(`${name}：${t('intake.diffReason')}`);
      }
      if (damaged > 0 && r.evidenceIds.length === 0) {
        return setError(`${name}：${t('intake.damageNeedPhoto')}`);
      }
    }

    setBusy(true);
    try {
      await api.verifyIntake(batch.id, {
        receivedDate: receivedDate ?? today,
        lines: lines.map((l) => ({
          lineId: l.id,
          receivedCartons: Number(row(l.id).receivedCartons),
          damagedCartons: Number(row(l.id).damagedCartons),
          note: row(l.id).note.trim() || null,
        })),
        damageEvidenceIds: lines.flatMap((l) => row(l.id).evidenceIds),
      });
      setDone(true);
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ ...card, padding: '16px' }}>
      <h2 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: 800 }}>{t('intake.verify')}</h2>
      <p style={hintStyle}>{t('intake.verifyHint')}</p>
      <p style={hintStyle}>{t('intake.damagedNote')}</p>

      <div style={{ margin: '10px 0' }}>
        <FormTextField
          id="f-verify-date"
          labelKey="intake.receivedDate"
          type="date"
          value={receivedDate}
          onChange={setReceivedDate}
        />
      </div>

      {lines.map((l) => (
        <IntakeVerifyRow
          key={l.id}
          line={l}
          row={row(l.id)}
          uploading={uploadingId === l.id}
          onChange={(patch) => patchRow(l.id, patch)}
          onPickPhoto={(file) => void uploadPhoto(l.id, file)}
        />
      ))}

      <button type="submit" style={btnPrimary} disabled={busy}>
        {busy ? t('intake.verifying') : t('intake.verify')}
      </button>
      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
      {done && (
        <p style={{ ...hintStyle, color: 'var(--color-primary)', fontWeight: 700 }}>
          {t('intake.verified')}
        </p>
      )}
    </form>
  );
}
